// Right-side menu guide — the element action rail + the layer stack beside it.
// Locale-keyed like every guide (content.<lang> → { feature, title, sections }).

const rightMenuGuide = {
  id: "right-menu",
  icon: "tune",
  keywords: [
    "right menu",
    "sağ menü",
    "element",
    "öğe",
    "settings",
    "ayarlar",
    "effect",
    "effects",
    "efekt",
    "tools",
    "araçlar",
    "edit text",
    "metni düzenle",
    "replace file",
    "dosyayı değiştir",
    "copy",
    "kopyala",
    "duplicate",
    "çoğalt",
    "delete",
    "sil",
    "layers",
    "katmanlar",
    "layer",
    "katman",
    "order",
    "sıra",
    "bring to front",
    "öne getir",
    "send to back",
    "arkaya gönder",
    "ungroup",
    "gruptan çıkar",
    "copy scene",
    "sahneyi kopyala",
    "lock",
    "kilitle",
    "hide",
    "gizle",
    "pin",
    "selected on top",
    "seçili öğe önde",
    "menú derecho",
    "ajustes",
    "efecto",
    "herramientas",
    "editar texto",
    "copiar",
    "duplicar",
    "eliminar",
    "capas",
    "capa",
    "traer al frente",
    "enviar al fondo",
    "desagrupar",
    "copiar escena",
    "bloquear",
    "ocultar",
    "seleccionado delante",
  ],
  content: {
    en: {
      feature: "Right menu",
      title: "Right-side menu & layers",
      sections: [
        {
          id: "element-menu",
          title: "The element menu",
          videoUrl: null,
          text:
            "The vertical menu on the right of the canvas is always there; it fades back while you're not using it. Its top buttons open panels — click the same button again to close one.\n\n" +
            "- **Scene** — stacking order, layers and scene copy (see The Scene card). Open by default.\n" +
            "- **Settings** — with nothing selected, the scene's settings; with an element selected, that element's properties. Images and videos show **Tools** instead; both have their own guides.\n" +
            "- **Effect** — entrance and exit effects for the selected element (see the Timeline & animation guide). A small dot on the icon means it already has one.\n" +
            "- **AI Wizard** — AI editing for an eligible image or recorded video; covered in its own guide.\n" +
            "- **Elements** — replaces Effect when an interactive element is selected.\n\n" +
            "When an element is selected, its actions appear below a divider. Only the ones that apply are shown:\n\n" +
            "- **Edit Text** — edit a text element's content in place.\n" +
            "- **Replace File** — swap the element's image, video or other file.\n" +
            "- **Plugin Settings**, **Fit to Screen**, **Add Answer**, **Increase Text** / **Decrease Text** — for elements that support them.\n" +
            "- **Copy style** — copy this element's look to other elements (see the Canvas basics guide).\n" +
            "- **Copy** — copy the element so you can paste it into another scene, or another training.\n" +
            "- **Duplicate** — drop an exact copy onto the same scene.\n" +
            "- **Delete** — remove the element (shown in red).",
        },
        {
          id: "layer-management",
          title: "Layer management",
          videoUrl: null,
          text:
            "Open the **Layers** list from the **Scene** card (the stacked-layers button). It lists every element on the scene, **front-most at the top**. Use it when elements overlap.\n\n" +
            "- **Reorder** by dragging a row's grip up or down — higher in the list means in front on the canvas.\n" +
            "- **Select** an element by clicking its row (easier than clicking a buried one).\n" +
            "- Same-kind media that share a timeline row appear as one group with a count; click it to expand its members. The group's eye and lock act on all of them.\n" +
            "- **Show / hide** an element with the eye. Hidden elements are hidden for learners too.\n" +
            "- **Lock** an element so it can't be moved or resized by accident. Locked rows can't be dragged.\n" +
            "- **Hide all** / **Show all** and **Lock all** / **Unlock all** in the list's header act on every layer at once.\n" +
            "- While an element is selected, the canvas shows it in front of the others so you can see it — even if **Layers** puts it behind. This is view-only: the order isn't changed, and the element drops back into place as soon as you deselect it.\n\n" +
            "Press Esc or click outside the list to close it.",
        },
        {
          id: "scene-card",
          title: "The Scene card",
          videoUrl: null,
          text:
            "**Scene** opens a compact card of scene-level actions. Hover a button to see its name.\n\n" +
            "- The four stacking buttons at the top — bring to front, bring forward, send backward, send to back — move the selected element in the stacking order.\n" +
            "- **Layers** opens the layer list (see Layer management).\n" +
            "- **Ungroup** splits a grouped smart element back into separate elements. It's active only when an element from such a group is selected.\n" +
            "- **Copy scene** copies the whole scene so you can paste it (Ctrl+V) into this or any other training. It's active only when no element is selected; the Scenes guide explains pasting.",
        },
      ],
    },
    tr: {
      feature: "Sağ Menü",
      title: "Sağ menü ve katmanlar",
      sections: [
        {
          id: "element-menu",
          title: "Öğe menüsü",
          videoUrl: null,
          text:
            "Tuvalin sağındaki dikey menü her zaman oradadır; kullanmadığınız sırada soluklaşır. Üstteki düğmeler panelleri açar — kapatmak için aynı düğmeye yeniden tıklayın.\n\n" +
            "- **Sahne** — katman sırası, katmanlar ve sahne kopyalama (bkz. Sahne kartı). Varsayılan olarak açıktır.\n" +
            "- **Ayarlar** — hiçbir şey seçili değilken sahnenin ayarları; bir öğe seçiliyken o öğenin özellikleri. Görsel ve videolarda bunun yerine **Araçlar** görünür; her ikisinin de kendi rehberi vardır.\n" +
            "- **Efekt** — seçili öğenin giriş ve çıkış efektleri (bkz. Zaman çizelgesi ve animasyon rehberi). Simgedeki küçük nokta, öğede zaten bir efekt olduğunu gösterir.\n" +
            "- **AI Wizard** (Yapay Zekâ Sihirbazı) — uygun bir görsel veya kaydedilmiş video için yapay zekâ ile düzenleme; kendi rehberinde anlatılır.\n" +
            "- **Öğeler** — etkileşimli bir öğe seçiliyken Efekt'in yerini alır.\n\n" +
            "Bir öğe seçiliyken, öğeye ait eylemler bir ayırıcının altında görünür. Yalnızca geçerli olanlar gösterilir:\n\n" +
            "- **Metni Düzenle** — bir metin öğesinin içeriğini yerinde düzenler.\n" +
            "- **Dosyayı Değiştir** — öğenin görselini, videosunu veya diğer dosyasını değiştirir.\n" +
            "- **Eklenti Ayarı**, **Ekrana Sığdır**, **Cevap Ekle**, **Metni Büyüt** / **Metni Küçült** — bunları destekleyen öğeler için.\n" +
            "- **Stili kopyala** — bu öğenin görünümünü diğer öğelere aktarır (bkz. Tuval temelleri rehberi).\n" +
            "- **Kopyala** — öğeyi başka bir sahneye ya da başka bir eğitime yapıştırmak için kopyalar.\n" +
            "- **Çoğalt** — aynı sahneye birebir kopyasını bırakır.\n" +
            "- **Sil** — öğeyi kaldırır (kırmızı gösterilir).",
        },
        {
          id: "layer-management",
          title: "Katman yönetimi",
          videoUrl: null,
          text:
            "**Katmanlar** listesini **Sahne** kartından (üst üste katmanlar simgeli düğme) açın. Liste sahnedeki her öğeyi **en öndeki en üstte** olacak şekilde gösterir. Öğeler üst üste bindiğinde kullanın.\n\n" +
            "- Bir satırın tutamacını yukarı/aşağı sürükleyerek **sırasını değiştirin** — listede daha yukarısı, tuvalde daha önde demektir.\n" +
            "- Bir satıra tıklayarak o öğeyi **seçin** (altta kalan bir öğeye tıklamaktan kolaydır).\n" +
            "- Zaman çizelgesinde aynı satırı paylaşan aynı türdeki medyalar, sayısıyla birlikte tek bir grup olarak görünür; üyelerini görmek için gruba tıklayın. Grubun gözü ve kilidi tüm üyelere uygulanır.\n" +
            "- Göz düğmesiyle bir öğeyi **Göster / Gizle**. Gizlenen öğeler öğrenciler için de gizlenir.\n" +
            "- Bir öğeyi yanlışlıkla taşınmasın veya yeniden boyutlandırılmasın diye **Kilitle**. Kilitli satırlar sürüklenemez.\n" +
            "- Listenin başlığındaki **Tümünü gizle** / **Tümünü göster** ve **Tümünü kilitle** / **Tümünün kilidini aç** her katmana tek seferde uygulanır.\n" +
            "- Bir öğe seçiliyken, görebilmeniz için tuvalde diğerlerinin önünde gösterilir — **Katmanlar** listesi onu arkaya koysa bile. Bu yalnızca görünümle ilgilidir: sıra değişmez ve seçimi kaldırdığınız anda öğe yerine döner.\n\n" +
            "Listeyi kapatmak için Esc'ye basın veya listenin dışına tıklayın.",
        },
        {
          id: "scene-card",
          title: "Sahne kartı",
          videoUrl: null,
          text:
            "**Sahne**, sahne düzeyindeki eylemleri içeren küçük bir kart açar. Adını görmek için bir düğmenin üzerine gelin.\n\n" +
            "- Üstteki dört sıralama düğmesi — **Öne Getir**, **İleri**, **Geri**, **Arkaya Gönder** — seçili öğeyi katman sırasında taşır.\n" +
            "- **Katmanlar** katman listesini açar (bkz. Katman yönetimi).\n" +
            "- **Gruptan Çıkar** gruplanmış bir akıllı öğeyi yeniden ayrı öğelere böler. Yalnızca böyle bir gruptan bir öğe seçiliyken etkindir.\n" +
            "- **Sahneyi kopyala** tüm sahneyi kopyalar; böylece onu (Ctrl+V ile) bu ya da başka bir eğitime yapıştırabilirsiniz. Yalnızca hiçbir öğe seçili değilken etkindir; yapıştırma Sahneler rehberinde anlatılır.",
        },
      ],
    },
    es: {
      feature: "Menú derecho",
      title: "Menú derecho y capas",
      sections: [
        {
          id: "element-menu",
          title: "El menú de elementos",
          videoUrl: null,
          text:
            "El menú vertical a la derecha del lienzo está siempre ahí; se atenúa mientras no lo usas. Sus botones superiores abren paneles: vuelve a hacer clic en el mismo botón para cerrar uno.\n\n" +
            "- **Escena** — orden de apilamiento, capas y copia de la escena (consulta La tarjeta Escena). Abierta por defecto.\n" +
            "- **Ajustes** — sin nada seleccionado, los ajustes de la escena; con un elemento seleccionado, las propiedades de ese elemento. Las imágenes y los vídeos muestran **Herramientas** en su lugar; ambos tienen su propia guía.\n" +
            "- **Efecto** — efectos de entrada y salida del elemento seleccionado (consulta la guía Línea de tiempo y animación). Un pequeño punto en el icono indica que ya tiene uno.\n" +
            "- **AI Wizard** — edición con IA para una imagen o un vídeo grabado compatibles; se explica en su propia guía.\n" +
            "- **Elementos** — sustituye a Efecto cuando hay un elemento interactivo seleccionado.\n\n" +
            "Cuando hay un elemento seleccionado, sus acciones aparecen bajo un separador. Solo se muestran las que se aplican:\n\n" +
            "- **Editar texto** — edita el contenido de un elemento de texto en el mismo lugar.\n" +
            "- **Reemplazar archivo** — cambia la imagen, el vídeo u otro archivo del elemento.\n" +
            "- **Ajustes del complemento**, **Ajustar a la pantalla**, **Añadir respuesta**, **Aumentar texto** / **Reducir texto** — para los elementos que los admiten.\n" +
            "- **Copiar estilo** — copia el aspecto de este elemento a otros elementos (consulta la guía Conceptos básicos del lienzo).\n" +
            "- **Copiar** — copia el elemento para pegarlo en otra escena o en otra formación.\n" +
            "- **Duplicar** — coloca una copia exacta en la misma escena.\n" +
            "- **Eliminar** — quita el elemento (se muestra en rojo).",
        },
        {
          id: "layer-management",
          title: "Gestión de capas",
          videoUrl: null,
          text:
            "Abre la lista **Capas** desde la tarjeta **Escena** (el botón de capas apiladas). Muestra todos los elementos de la escena, **con el que está más al frente arriba**. Úsala cuando los elementos se superpongan.\n\n" +
            "- **Reordena** arrastrando el asa de una fila hacia arriba o hacia abajo: más arriba en la lista significa más al frente en el lienzo.\n" +
            "- **Selecciona** un elemento haciendo clic en su fila (es más fácil que hacer clic en uno que ha quedado tapado).\n" +
            "- Los medios del mismo tipo que comparten una fila de la línea de tiempo aparecen como un grupo con un contador; haz clic en él para desplegar sus miembros. El ojo y el candado del grupo actúan sobre todos ellos.\n" +
            "- **Muestra u oculta** un elemento con el ojo. Los elementos ocultos también se ocultan para los alumnos.\n" +
            "- **Bloquea** un elemento para que no se pueda mover ni redimensionar por accidente. Las filas bloqueadas no se pueden arrastrar.\n" +
            "- **Ocultar todo** / **Mostrar todo** y **Bloquear todo** / **Desbloquear todo**, en la cabecera de la lista, actúan sobre todas las capas a la vez.\n" +
            "- Mientras un elemento está seleccionado, el lienzo lo muestra delante de los demás para que puedas verlo, aunque **Capas** lo coloque detrás. Es solo visual: el orden no cambia y el elemento vuelve a su sitio en cuanto lo deseleccionas.\n\n" +
            "Pulsa Esc o haz clic fuera de la lista para cerrarla.",
        },
        {
          id: "scene-card",
          title: "La tarjeta Escena",
          videoUrl: null,
          text:
            "**Escena** abre una tarjeta compacta con acciones a nivel de escena. Pasa el ratón sobre un botón para ver su nombre.\n\n" +
            "- Los cuatro botones de apilamiento de la parte superior (traer al frente, adelantar, retroceder y enviar al fondo) mueven el elemento seleccionado en el orden de apilamiento.\n" +
            "- **Capas** abre la lista de capas (consulta Gestión de capas).\n" +
            "- **Desagrupar** separa un elemento inteligente agrupado en elementos independientes. Solo está activo cuando hay seleccionado un elemento de un grupo así.\n" +
            "- **Copiar escena** copia toda la escena para que puedas pegarla (Ctrl+V) en esta o en cualquier otra formación. Solo está activo cuando no hay ningún elemento seleccionado; la guía Escenas explica cómo pegarla.",
        },
      ],
    },
  },
};

export default rightMenuGuide;
